import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { GripVertical } from 'lucide-react';
import { api, formatApiErrorDetail } from '../../lib/api';
import { invalidateModulesConfig } from '../../lib/modules';

const MODULE_META = {
  hotels:    { label: 'Hotels',    emoji: '🏨', description: 'Live Hotelbeds inventory · Razorpay checkout' },
  flights:   { label: 'Flights',   emoji: '✈️', description: 'Domestic & international flights' },
  trains:    { label: 'Trains',    emoji: '🚄', description: 'IRCTC / Indian Railways' },
  buses:     { label: 'Buses',     emoji: '🚌', description: 'Redbus / AbhiBus partner inventory' },
  cabs:      { label: 'Cabs',      emoji: '🚕', description: 'Outstation, local & airport transfers' },
  villas:    { label: 'Villas',    emoji: '🏡', description: 'Vacation homes & private villas' },
  packages:  { label: 'Packages',  emoji: '🎒', description: 'Curated holiday packages' },
  tours:     { label: 'Tours',     emoji: '🗺️', description: 'Attractions, day-trips, activities' },
  visa:      { label: 'Visa',      emoji: '📋', description: 'Visa applications & documentation' },
  cruise:    { label: 'Cruise',    emoji: '🚢', description: 'River and ocean cruises' },
  forex:     { label: 'Forex',     emoji: '💱', description: 'Foreign exchange & travel cards' },
  insurance: { label: 'Insurance', emoji: '🛡️', description: 'Travel insurance plans' },
};
const ALL_KEYS = Object.keys(MODULE_META);

const AdminModules = () => {
  const [modules, setModules] = useState({});
  const [order, setOrder] = useState(ALL_KEYS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get('/admin/modules/')
      .then((r) => {
        setModules(r.data.modules || {});
        setOrder(Array.isArray(r.data.order) && r.data.order.length ? r.data.order : ALL_KEYS);
      })
      .catch(() => toast.error('Failed to load modules'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (key) => {
    setModules((prev) => ({ ...prev, [key]: !prev[key] }));
    setDirty(true);
  };

  // ─── Native HTML5 drag-and-drop ──────────────────────────────────────
  const dragKey = useRef(null);
  const overKey = useRef(null);
  const [dragKeyState, setDragKeyState] = useState(null);
  const [overKeyState, setOverKeyState] = useState(null);

  const onDragStart = (e, key) => {
    dragKey.current = key;
    setDragKeyState(key);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', key); } catch { /* IE noop */ }
  };

  const onDragOver = (e, key) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overKey.current !== key) {
      overKey.current = key;
      setOverKeyState(key);
    }
  };

  const onDrop = (e, key) => {
    e.preventDefault();
    const src = dragKey.current;
    dragKey.current = null;
    overKey.current = null;
    setDragKeyState(null);
    setOverKeyState(null);
    if (!src || src === key) return;
    setOrder((prev) => {
      const next = [...prev];
      const srcIdx = next.indexOf(src);
      const tgtIdx = next.indexOf(key);
      if (srcIdx < 0 || tgtIdx < 0) return prev;
      next.splice(srcIdx, 1);
      next.splice(tgtIdx, 0, src);
      return next;
    });
    setDirty(true);
  };

  const onDragEnd = () => {
    dragKey.current = null;
    overKey.current = null;
    setDragKeyState(null);
    setOverKeyState(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/admin/modules/', { modules, order });
      setModules(r.data.modules);
      setOrder(r.data.order || order);
      setDirty(false);
      invalidateModulesConfig();
      toast.success('Saved · Module visibility & order updated on the website.');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px] text-gray-500">Loading modules…</div>;
  }

  const enabledCount = Object.values(modules).filter(Boolean).length;

  return (
    <div className="space-y-6" data-testid="admin-modules-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Service Modules</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Toggle visibility AND drag-reorder by position. The order here drives the website's homepage,
            navbar, and results-page tab order. Disabled modules are hidden everywhere.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center rounded-full border border-gray-300 dark:border-gray-700 px-3 py-1 text-sm text-gray-700 dark:text-gray-300" data-testid="modules-enabled-count">
            {enabledCount} of {ALL_KEYS.length} live
          </span>
          <button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="modules-save-btn"
            className="inline-flex items-center gap-2 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 grid grid-cols-[24px_40px_60px_1fr_140px] gap-3 items-center text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <span></span>
          <span>#</span>
          <span></span>
          <span>Module — drag rows to reorder</span>
          <span className="text-right">Status</span>
        </div>
        {order.map((key, idx) => {
          const meta = MODULE_META[key];
          if (!meta) return null;
          const enabled = !!modules[key];
          const isDragging = dragKeyState === key;
          const isOver = overKeyState === key && dragKeyState !== key;
          return (
            <div
              key={key}
              data-testid={`module-row-${key}`}
              draggable
              onDragStart={(e) => onDragStart(e, key)}
              onDragOver={(e) => onDragOver(e, key)}
              onDrop={(e) => onDrop(e, key)}
              onDragEnd={onDragEnd}
              className={`grid grid-cols-[24px_40px_60px_1fr_140px] gap-3 items-center px-4 py-3 border-b border-gray-100 dark:border-gray-800 last:border-0 cursor-grab active:cursor-grabbing
                ${enabled ? '' : 'opacity-60'}
                ${isDragging ? 'opacity-40 bg-red-50 dark:bg-red-950/30' : ''}
                ${isOver ? 'border-t-2 border-t-red-500 bg-red-50/40 dark:bg-red-950/20' : ''}
              `}
            >
              <GripVertical
                data-testid={`module-drag-${key}`}
                className="h-4 w-4 text-gray-400 dark:text-gray-500 shrink-0"
                aria-label={`Drag ${meta.label}`}
              />
              <span className="text-sm font-bold text-gray-400 dark:text-gray-500">{idx + 1}</span>
              <div className={`p-2 rounded-lg text-xl leading-none ${enabled ? 'bg-red-100 dark:bg-red-950' : 'bg-gray-100 dark:bg-gray-800'} w-fit`}>
                {meta.emoji}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{meta.label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{meta.description}</p>
              </div>
              <div className="flex items-center justify-end gap-3">
                <span className={`text-[11px] font-bold uppercase tracking-wide ${enabled ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
                  {enabled ? 'LIVE' : 'OFF'}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  onClick={() => toggle(key)}
                  data-testid={`module-toggle-${key}`}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors ${enabled ? 'bg-red-600' : 'bg-gray-300 dark:bg-gray-700'}`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {dirty && (
        <div className="fixed bottom-6 right-6 z-40 bg-white dark:bg-gray-800 shadow-2xl rounded-xl border border-amber-200 dark:border-amber-800 px-4 py-3 flex items-center gap-3">
          <div className="text-sm text-gray-700 dark:text-gray-200">Unsaved changes</div>
          <button
            onClick={save}
            disabled={saving}
            className="rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold px-3 py-1.5 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminModules;
